import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import {
  Loader2, CheckCircle, XCircle, RefreshCw, ExternalLink,
  Linkedin, Globe, Palette, MessageSquare, ArrowLeft,
  Wifi, WifiOff, Link2, Unlink
} from 'lucide-react';

const API = '/api';

const PLATFORM_CONFIG = {
  linkedin: {
    name: 'LinkedIn',
    icon: Linkedin,
    color: 'text-blue-700',
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    description: 'Publish posts and articles to your LinkedIn profile',
    authUrl: `${API}/social/publish/linkedin/auth-url`,
  },
  facebook: {
    name: 'Facebook',
    icon: () => (
      <svg className="h-5 w-5 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
      </svg>
    ),
    color: 'text-blue-600',
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    description: 'Publish to Facebook Pages and manage post insights',
    authUrl: `${API}/social/publish/meta/auth-url`,
    supportsAutoConfig: true,
  },
  instagram: {
    name: 'Instagram',
    icon: () => (
      <svg className="h-5 w-5 text-pink-600" fill="currentColor" viewBox="0 0 24 24">
        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
      </svg>
    ),
    color: 'text-pink-600',
    bg: 'bg-pink-50',
    border: 'border-pink-200',
    description: 'Post images & reels to your Instagram Business account',
    note: 'Requires Facebook Page with linked IG Business account',
    authUrl: null, // IG connects via Meta OAuth
    supportsAutoConfig: true,
  },
  wordpress: {
    name: 'WordPress',
    icon: Globe,
    color: 'text-cyan-700',
    bg: 'bg-cyan-50',
    border: 'border-cyan-200',
    description: 'Publish blog posts directly to your WordPress site',
    note: 'Configured via environment variables (WP_URL, credentials)',
    authUrl: null,
  },
  canva: {
    name: 'Canva',
    icon: Palette,
    color: 'text-teal-600',
    bg: 'bg-teal-50',
    border: 'border-teal-200',
    description: 'Design images in Canva and import them back into the app',
    authUrl: `${API}/social/canva/auth-url`,
  },
};

export default function Connections() {
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const [connecting, setConnecting] = useState({});
  const [showTokenInput, setShowTokenInput] = useState(false);
  const [tokenInput, setTokenInput] = useState('');
  const [updatingToken, setUpdatingToken] = useState(false);
  const [refreshingToken, setRefreshingToken] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    testAll();
    // Show success/error from OAuth redirect
    const params = new URLSearchParams(window.location.search);
    const connected = params.get('connected');
    const error = params.get('error');
    if (connected) {
      toast.success(`${connected.charAt(0).toUpperCase() + connected.slice(1)} connected successfully!`);
      window.history.replaceState({}, '', '/social/connections');
    }
    if (error) {
      toast.error(`Connection failed: ${decodeURIComponent(error)}`);
      window.history.replaceState({}, '', '/social/connections');
    }
  }, []);

  const testAll = async () => {
    setTesting(true);
    try {
      const { data } = await axios.post(`${API}/social/publish/test-connections`, {}, { withCredentials: true });
      setResults(data);
    } catch (e) {
      toast.error('Failed to check connections');
    } finally {
      setLoading(false);
      setTesting(false);
    }
  };

  const connectPlatform = async (platform) => {
    const config = PLATFORM_CONFIG[platform];
    if (!config?.authUrl) return;
    setConnecting(p => ({ ...p, [platform]: true }));
    try {
      const { data } = await axios.get(config.authUrl, { withCredentials: true });
      const url = data.auth_url || data.url;
      if (url) {
        window.open(url, '_blank', 'width=600,height=700');
        toast.info(`Complete ${config.name} authorization in the popup. Then click "Refresh Status" here.`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || `Failed to start ${config.name} authorization`);
    } finally {
      setConnecting(p => ({ ...p, [platform]: false }));
    }
  };

  const updateMetaToken = async () => {
    if (!tokenInput.trim()) return;
    setUpdatingToken(true);
    try {
      const { data } = await axios.post(`${API}/social/meta/update-token`, { access_token: tokenInput.trim() }, { withCredentials: true });
      toast.success(data.message);
      setShowTokenInput(false);
      setTokenInput('');
      testAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Token update failed');
    } finally {
      setUpdatingToken(false);
    }
  };

  const refreshMetaToken = async () => {
    setRefreshingToken(true);
    try {
      const { data } = await axios.post(`${API}/social/meta/refresh-token`, {}, { withCredentials: true });
      toast.success(data.message);
      testAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Token refresh failed');
    } finally {
      setRefreshingToken(false);
    }
  };

  const platforms = results?.platforms || {};
  const summary = results?.summary || {};

  return (
    <div className="max-w-3xl mx-auto space-y-6 p-4 sm:p-6" data-testid="connections-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-xs text-slate-400 hover:text-slate-600 mb-1">
            <ArrowLeft className="h-3 w-3" /> Back
          </button>
          <h1 className="text-xl font-semibold text-slate-800 tracking-tight">Platform Connections</h1>
          <p className="text-sm text-slate-500 mt-0.5">Connect your accounts to publish and sync insights</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={testAll}
          disabled={testing}
          data-testid="refresh-connections-btn"
        >
          <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${testing ? 'animate-spin' : ''}`} />
          {testing ? 'Checking...' : 'Refresh Status'}
        </Button>
      </div>

      {/* Summary */}
      {!loading && results && (
        <div className="flex gap-3" data-testid="connections-summary">
          <div className="flex items-center gap-2 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-lg">
            <Wifi className="h-4 w-4 text-emerald-600" />
            <span className="text-sm font-medium text-emerald-700">{summary.ready_to_publish} Connected</span>
          </div>
          <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg">
            <WifiOff className="h-4 w-4 text-slate-400" />
            <span className="text-sm font-medium text-slate-500">{summary.needs_attention} Need Attention</span>
          </div>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
        </div>
      )}

      {/* Platform Cards */}
      {!loading && (
        <div className="space-y-3">
          {Object.entries(PLATFORM_CONFIG).map(([key, config]) => {
            const status = platforms[key] || {};
            const connected = status.can_post;
            const IconComponent = config.icon;

            return (
              <Card key={key} className={`border ${connected ? config.border : 'border-slate-200'} transition-colors`} data-testid={`connection-${key}`}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-4">
                    {/* Icon */}
                    <div className={`w-12 h-12 rounded-xl ${config.bg} flex items-center justify-center shrink-0`}>
                      <IconComponent className={`h-6 w-6 ${config.color}`} />
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <h3 className="text-sm font-semibold text-slate-800">{config.name}</h3>
                        {connected ? (
                          <Badge className="bg-emerald-100 text-emerald-700 text-[10px] gap-1">
                            <CheckCircle className="h-2.5 w-2.5" /> Connected
                          </Badge>
                        ) : status.status === 'expired' ? (
                          <Badge className="bg-amber-100 text-amber-700 text-[10px] gap-1">
                            <XCircle className="h-2.5 w-2.5" /> Expired
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-slate-400 text-[10px]">Not connected</Badge>
                        )}
                      </div>
                      <p className="text-xs text-slate-500">{config.description}</p>
                      {config.note && <p className="text-[10px] text-slate-400 mt-0.5">{config.note}</p>}

                      {/* Status message */}
                      {status.message && (
                        <p className={`text-xs mt-1.5 ${connected ? 'text-emerald-600' : 'text-slate-400'}`}>
                          {status.message}
                        </p>
                      )}

                      {/* Extra info for connected platforms */}
                      {connected && status.user && (
                        <p className="text-xs text-slate-500 mt-1">Account: <span className="font-medium">{status.user}</span></p>
                      )}
                      {connected && status.pages && (
                        <p className="text-xs text-slate-500 mt-1">Pages: <span className="font-medium">{status.pages.join(', ')}</span></p>
                      )}
                    </div>

                    {/* Action */}
                    <div className="shrink-0">
                      {config.authUrl && !connected && !(config.supportsAutoConfig && status.auto_configured) && (
                        <Button
                          size="sm"
                          onClick={() => connectPlatform(key)}
                          disabled={connecting[key]}
                          className="gap-1.5"
                          data-testid={`connect-${key}-btn`}
                        >
                          {connecting[key] ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <Link2 className="h-3 w-3" />
                          )}
                          Connect
                        </Button>
                      )}
                      {config.authUrl && connected && !(config.supportsAutoConfig && connected) && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => connectPlatform(key)}
                          disabled={connecting[key]}
                          className="gap-1.5 text-xs"
                          data-testid={`reconnect-${key}-btn`}
                        >
                          <RefreshCw className="h-3 w-3" /> Reconnect
                        </Button>
                      )}
                      {/* Auto-configured badge for env-based tokens */}
                      {connected && (config.supportsAutoConfig || (!config.authUrl && key === 'wordpress')) && (
                        <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">
                          <CheckCircle className="h-2.5 w-2.5 mr-1" /> Auto-configured
                        </Badge>
                      )}
                      {!connected && !config.authUrl && key === 'instagram' && (
                        <span className="text-[10px] text-slate-400 block text-right">Via Facebook</span>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Help text */}
      <div className="text-xs text-slate-400 text-center pt-4 border-t border-slate-100">
        After connecting, use "Refresh Status" to verify. Publishing is done from approved content pages.
      </div>

      {/* Meta Token Management */}
      <Card className="border-amber-200 bg-amber-50/30" data-testid="meta-token-card">
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold text-slate-800">Meta Token Management</h3>
              <p className="text-xs text-slate-500 mt-0.5">
                Paste a User Access Token to auto-derive <strong>permanent</strong> Page tokens
              </p>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" className="text-xs gap-1.5"
                onClick={refreshMetaToken} disabled={refreshingToken}
                data-testid="refresh-meta-token-btn"
              >
                {refreshingToken ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                Refresh Token
              </Button>
              <Button size="sm" className="text-xs gap-1.5"
                onClick={() => setShowTokenInput(!showTokenInput)}
                data-testid="update-meta-token-toggle"
              >
                <Link2 className="h-3 w-3" />
                {showTokenInput ? 'Cancel' : 'Update Token'}
              </Button>
            </div>
          </div>

          {showTokenInput && (
            <div className="space-y-2" data-testid="meta-token-form">
              <div className="flex gap-2">
                <input
                  type="password"
                  value={tokenInput}
                  onChange={e => setTokenInput(e.target.value)}
                  placeholder="Paste your User Access Token from Graph API Explorer"
                  className="flex-1 px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  data-testid="meta-token-input"
                />
                <Button size="sm" onClick={updateMetaToken} disabled={updatingToken || !tokenInput.trim()}
                  data-testid="meta-token-submit-btn"
                >
                  {updatingToken ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Connect'}
                </Button>
              </div>
              <div className="text-[10px] text-slate-400 space-y-0.5">
                <p>1. Go to <a href="https://developers.facebook.com/tools/explorer/" target="_blank" rel="noopener noreferrer" className="text-blue-500 underline">Graph API Explorer</a></p>
                <p>2. Select your app, click "Generate Access Token"</p>
                <p>3. Add permissions: <code className="bg-slate-100 px-1 rounded">pages_manage_posts, pages_read_engagement, instagram_basic, instagram_content_publish</code></p>
                <p>4. Copy the token and paste above</p>
                <p className="text-amber-600 font-medium mt-1">The app will auto-exchange this for permanent page tokens that never expire.</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
